import type { BookingStatusCounts } from '../api/schemas';

/** Pure transforms behind the Historial dashboard: no fetching, no React. */

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const NO_DATA_LABEL = 'Sin datos';

export type CueCounts = { shown: number; acknowledged: number };

/**
 * The pilot's headline number (docs/HANDOFF-AI.md §6.2): of the cues shown,
 * how many did someone act on. Undefined, not 0 or NaN, when nothing was
 * shown, so callers can render a placeholder instead of a misleading "0%".
 */
export function computeAckRate(cues: CueCounts): number | undefined {
  if (cues.shown <= 0) return undefined;
  return cues.acknowledged / cues.shown;
}

/** Renders an ack rate as a rounded whole percent, or a placeholder when there is none. */
export function formatAckRatePercent(rate: number | undefined): string {
  if (rate === undefined) return NO_DATA_LABEL;
  return `${Math.round(rate * 100)}%`;
}

/** Sums bookings across every status, to tell "no bookings in range" from "loading". */
export function totalBookings(counts: BookingStatusCounts): number {
  return Object.values(counts).reduce((sum, count) => sum + count, 0);
}

export type MetricsRangeParams = { from?: string; to?: string };

/**
 * Converts the two `<input type="date">` values (plain `YYYY-MM-DD`, no
 * time) into the half-open `[from, to)` ISO instant range the metrics
 * endpoint expects. `to` is pushed to the start of the following day so the
 * selected end date is included in full. Both bounds are read as UTC
 * midnight: a timezone-aware picker is a later design pass, not part of this
 * contract.
 */
export function dateInputToRange(from?: string, to?: string): MetricsRangeParams {
  return {
    ...(from ? { from: `${from}T00:00:00.000Z` } : {}),
    ...(to ? { to: new Date(new Date(`${to}T00:00:00.000Z`).getTime() + MS_PER_DAY).toISOString() } : {}),
  };
}
